import { useApolloClient } from '@apollo/client';
import { FormField, FormHelp, Label, UnstyledButton } from '@zillow/constellation-10';
import { Adornment, IconCloseCircle, IconSearch, SearchBox } from '@zillow/constellation';
import { Fragment, useContext } from 'react';
import { ConversationList_GetConversationsDocument, ReadRepliedStatus } from '../../../../types/GraphqlTypes';
import useInboxRouterHelper from '../../../../hooks/useInboxRouterHelper';
import { MAX_OLD_CONVERSATION_DAYS } from '../../../../constants/ConfigConstants';
import usePropertyFilter, {
  PropertyListingItem,
  UpdatePathWithListingAlias,
} from '../../../../hooks/usePropertyFilter';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { Skeleton, SkeletonListItem } from './Skeleton';
import { StyledCombobox, StyledComboboxOption, StyledInput } from './StyledComponents';
import { IsVisible } from '@zg-rentals/react-use';

type ComboboxInputProps = {
  autoCompleteBehavior: 'none' | 'automatic' | 'manual';
  focusFirstOption: boolean;
  optionFilter: null;
  options: PropertyListingItem[] | [];
  onClear?(): void;
  onTagClose?(): void;
};

const PropertySearchBar = () => {
  const apolloClient = useApolloClient();
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const { clientTimeZone, daysBack, folder, listingAlias, isByProperty, readRepliedStatus, renterProgressStatus } =
    useContext(InboxPageContext);

  const resetPropertyFilter = async () => {
    if (readRepliedStatus !== ReadRepliedStatus.ShowAll) {
      await apolloClient.query({
        query: ConversationList_GetConversationsDocument,
        variables: {
          listingAlias: null,
          clientTimeZone,
          daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
          folder,
          renterProgressStatus,
          readRepliedStatus,
        },
        fetchPolicy: 'network-only',
      });
    }

    await changeFiltersOrFolders({
      folder,
      readRepliedStatus,
      renterProgressStatus,
      byProperty: false,
      listingAlias: undefined,
    });
  };

  const updatePathWithListingAlias: UpdatePathWithListingAlias = async (listingAlias) => {
    await changeFiltersOrFolders({
      folder,
      readRepliedStatus,
      renterProgressStatus,
      byProperty: true,
      listingAlias,
    });
  };

  const {
    errorMessage,
    handleChange,
    handleScroll,
    handleSelectProperty,
    hasMoreToPaginate,
    highlightPatternRegex,
    propertyListings,
    searchText,
    shouldShowSkeleton,
  } = usePropertyFilter({
    updatePathWithListingAlias,
    resetPropertyFilter,
    defaultListingAliasOnLoad: isByProperty && listingAlias ? listingAlias : undefined,
  });

  if (!errorMessage && shouldShowSkeleton) {
    return <Skeleton />;
  }

  return (
    <FormField
      error={!!errorMessage}
      label={<Label css={{ paddingBottom: 'tightest' }}>Property</Label>}
      description={<FormHelp>{errorMessage}</FormHelp>}
      control={
        <StyledCombobox
          autoCompleteBehavior="manual"
          focusFirstOption={true}
          freeForm
          onChange={handleChange}
          onOptionSelect={handleSelectProperty}
          optionFilter={null} // Filtering happens at the API level
          options={propertyListings}
          value={searchText}
          /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
          renderInput={({ onClear, onTagClose, ...rest }: ComboboxInputProps) => {
            return (
              <SearchBox
                input={<StyledInput {...rest} placeholder="Search by property address" size="sm" />}
                leftAdornment={
                  <Adornment>
                    <IconSearch />
                  </Adornment>
                }
                rightAdornment={
                  <Adornment>
                    {searchText && (
                      <UnstyledButton onClick={onClear} title="Clear" css={{ display: 'flex' }}>
                        <IconCloseCircle />
                      </UnstyledButton>
                    )}
                  </Adornment>
                }
              />
            );
          }}
          renderOption={(props: { [key: string]: any }) => {
            const { id } = props;
            const isLastItemInList = id === propertyListings[propertyListings.length - 1].id;

            return (
              <Fragment key={id}>
                <StyledComboboxOption {...props} highlightPattern={highlightPatternRegex} />

                {isLastItemInList && hasMoreToPaginate && (
                  <IsVisible onChange={handleScroll} options={{ rootMargin: '0px 0px 200px 0px' }}>
                    {({ ref }) => <SkeletonListItem ref={ref} />}
                  </IsVisible>
                )}
              </Fragment>
            );
          }}
        />
      }
    />
  );
};

export default PropertySearchBar;
